// components/billings/invoices-list.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./billings.styles.scss";

import { FiChevronDown, FiChevronRight, FiTrash2 } from "react-icons/fi";

import { db, updateInvoicePaidStatus } from "../../firebase/firebase.utils";

import InlineAddBtn from "../InlineAddBtn";

import {
  formatCurrencyAmount,
  formatCurrencyTotals,
  formatDocumentDate,
} from "./billing.utils";

function getDateMilliseconds(value) {
  if (!value) return 0;

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value.toDate === "function") {
    return value.toDate().getTime();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const parsedDate = new Date(value);
  const milliseconds = parsedDate.getTime();

  return Number.isNaN(milliseconds) ? 0 : milliseconds;
}

export default function InvoicesList({
  spaceId,
  clientId,
  currentUserId,
  periodRange,
  filterByPeriod = false,
  allSpaceBillings = false,
  onOpenInvoice,
  onAddInvoice,
  showDelete = true,
  setPaidRevenueByCurrency,
  setUnpaidRevenueByCurrency,
}) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [showInvoices, setShowInvoices] = useState(true);
  const [showReceipts, setShowReceipts] = useState(true);

  useEffect(() => {
    if (!spaceId || (!allSpaceBillings && !clientId)) {
      setInvoices([]);
      setLoading(false);
      return undefined;
    }

    setInvoices([]);
    setLoading(true);
    setLoadError("");

    let invoicesQuery = db
      .collection("space")
      .doc(spaceId)
      .collection("invoices");

    // Client/lead context:
    // retrieve every invoice associated with this person.
    if (!allSpaceBillings) {
      invoicesQuery = invoicesQuery.where("clientId", "==", clientId);
    }

    // Workspace Billing context:
    // apply its selected period.
    if (filterByPeriod && periodRange?.start) {
      invoicesQuery = invoicesQuery.where("issueDate", ">=", periodRange.start);
    }

    if (filterByPeriod && periodRange?.end) {
      invoicesQuery = invoicesQuery.where("issueDate", "<=", periodRange.end);
    }

    // Required for the date-range query.
    // For client/lead context, sorting happens locally instead.
    if (filterByPeriod) {
      invoicesQuery = invoicesQuery.orderBy("issueDate", "desc");
    }

    const unsubscribe = invoicesQuery.onSnapshot(
      (snapshot) => {
        const data = snapshot.docs
          .map((document) => ({
            ...document.data(),
            id: document.id,
          }))
          .sort(
            (invoiceA, invoiceB) =>
              getDateMilliseconds(invoiceB.issueDate) -
              getDateMilliseconds(invoiceA.issueDate),
          );

        setInvoices(data);
        setLoading(false);
      },
      (error) => {
        console.error("Invoices listener error:", error);
        setLoadError("Invoices could not be loaded.");
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [spaceId, clientId, allSpaceBillings, filterByPeriod, periodRange]);

  const periodLabel = filterByPeriod ? " for this period" : "";

  const revenueSummary = useMemo(() => {
    const paid = {};
    const unpaid = {};

    invoices.forEach((invoice) => {
      const currencyCode = invoice.currencyCode || "USD";
      const amount = Number(invoice?.totals?.subtotal || 0);
      const destination = invoice.paid ? paid : unpaid;

      destination[currencyCode] =
        Number(destination[currencyCode] || 0) + amount;
    });

    return {
      paid,
      unpaid,
    };
  }, [invoices]);

  useEffect(() => {
    setPaidRevenueByCurrency?.(revenueSummary.paid);
    setUnpaidRevenueByCurrency?.(revenueSummary.unpaid);
  }, [revenueSummary, setPaidRevenueByCurrency, setUnpaidRevenueByCurrency]);

  const unpaidInvoices = useMemo(
    () => invoices.filter((invoice) => !invoice.paid),
    [invoices],
  );

  const receipts = useMemo(
    () => invoices.filter((invoice) => invoice.paid),
    [invoices],
  );

  const handlePaidStatusChange = async (invoice, paidStatus) => {
    if (!currentUserId) {
      window.alert("Your user information is not available.");
      return;
    }

    try {
      await updateInvoicePaidStatus(
        currentUserId,
        spaceId,
        invoice.id,
        paidStatus,
      );
    } catch (error) {
      console.error("Invoice status update error:", error);
      window.alert(
        error?.message || "The invoice status could not be updated.",
      );
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    const invoiceName = invoice.invoiceNumber || "this invoice";

    const shouldDelete = window.confirm(
      `Delete ${invoiceName} from this workspace?`,
    );

    if (!shouldDelete) return;

    try {
      await db
        .collection("space")
        .doc(spaceId)
        .collection("invoices")
        .doc(invoice.id)
        .delete();
    } catch (error) {
      console.error("Delete invoice error:", error);
      window.alert(error?.message || "The invoice could not be deleted.");
    }
  };

  const renderInvoice = (invoice) => {
    const amount = invoice?.totals?.subtotal;
    const rightLabel =
      amount !== undefined && amount !== null
        ? formatCurrencyAmount(invoice.currencyCode, amount)
        : formatDocumentDate(invoice.issueDate);

    return (
      <div className="billing-row" key={invoice.id}>
        <button
          type="button"
          className={`billing-row__main ${
            onOpenInvoice ? "billing-row__main--clickable" : ""
          }`}
          onClick={() => onOpenInvoice?.(invoice, spaceId)}
        >
          <span className="billing-row__details">
            <strong>{invoice.invoiceNumber || "Invoice"}</strong>

            <span className="billing-row__meta">
              {invoice.clientName || ""}

              {invoice.clientName && invoice.issueDate ? " • " : ""}

              {formatDocumentDate(invoice.issueDate)}
            </span>
          </span>

          <strong className="billing-row__amount">{rightLabel}</strong>
        </button>

        {showDelete && (
          <button
            type="button"
            className="billing-row__icon-button"
            aria-label={`Delete ${invoice.invoiceNumber || "invoice"}`}
            onClick={() => handleDeleteInvoice(invoice)}
          >
            <FiTrash2 />
          </button>
        )}

        <label className="billing-paid-toggle">
          <input
            type="checkbox"
            checked={Boolean(invoice.paid)}
            onChange={(event) =>
              handlePaidStatusChange(invoice, event.target.checked)
            }
          />

          <span>Paid</span>
        </label>
      </div>
    );
  };

  if (loading) {
    return <div className="billing-loading">Loading invoices…</div>;
  }

  return (
    <div className="billing-list">
      {loadError && <div className="billing-error">{loadError}</div>}

      <section className="billing-section">
        <button
          type="button"
          className="billing-section__header"
          onClick={() => setShowInvoices((current) => !current)}
        >
          <span>
            <strong>Invoices</strong>

            <small>
              Expected revenue: {formatCurrencyTotals(revenueSummary.unpaid)}
            </small>
          </span>

          {showInvoices ? <FiChevronDown /> : <FiChevronRight />}
        </button>

        {showInvoices && (
          <div className="billing-section__content">
            {unpaidInvoices.length > 0 ? (
              unpaidInvoices.map(renderInvoice)
            ) : (
              <div className="billing-empty">
                No unpaid invoices found{periodLabel}.
              </div>
            )}

            {onAddInvoice && (
              <InlineAddBtn
                className="billing-inline-add"
                circleSize={32}
                iconSize={20}
                onClick={() => onAddInvoice(clientId || "", false)}
              >
                Add invoice
              </InlineAddBtn>
            )}
          </div>
        )}
      </section>

      <section className="billing-section">
        <button
          type="button"
          className="billing-section__header"
          onClick={() => setShowReceipts((current) => !current)}
        >
          <span>
            <strong>Receipts</strong>

            <small>Revenue: {formatCurrencyTotals(revenueSummary.paid)}</small>
          </span>

          {showReceipts ? <FiChevronDown /> : <FiChevronRight />}
        </button>

{showReceipts && (
  <div className="billing-section__content">
    {receipts.length > 0 ? (
      receipts.map(renderInvoice)
    ) : (
      <div className="billing-empty">
        No receipts found{periodLabel}.
      </div>
    )}

    {onAddInvoice && (
      <InlineAddBtn
        className="billing-inline-add"
        circleSize={32}
        iconSize={20}
        onClick={() => onAddInvoice(clientId || "", true)}
      >
        Add receipt
      </InlineAddBtn>
    )}
  </div>
)}
      </section>
    </div>
  );
}
